/*
 * Risk Taker Redemption engine — pure logic for the redemption page.
 *
 * The single importable seam for the page's product/size logic. Contains NO
 * Vue, NO DOM references and NO user-facing copy: every function is pure and
 * takes the serialised Shopify product JSON as an argument, and the add-to-cart
 * state comes back as a key the section maps to merchant-editable labels.
 *
 * The page sells one surprise design across four products — a kids and an adult
 * product per gender — behind a single Male/Female toggle and one size grid that
 * is split into a kids band and an adult band (Figma node 12175-25497). Picking
 * a size therefore picks the *product* as well as the variant, which is why a
 * size key is scoped to its group rather than being a bare variant id.
 *
 * Ships to the browser as a plain asset (no build step) and attaches its API to
 * `window.KnobbyRiskTakerEngine`; imported directly by tests as a CommonJS
 * module via `module.exports`.
 */
;((global) => {
  'use strict'

  // --- Genders ------------------------------------------------------------

  // `kidsRole`/`adultsRole` name the keys of the products object the section
  // serialises, so the roles are declared in exactly one place.
  const GENDERS = [
    {
      key: 'male',
      label: 'Male',
      kidsRole: 'boys',
      kidsLabel: 'Boys',
      adultsRole: 'mens',
      adultsLabel: 'Mens'
    },
    {
      key: 'female',
      label: 'Female',
      kidsRole: 'girls',
      kidsLabel: 'Girls',
      adultsRole: 'womens',
      adultsLabel: 'Womens'
    }
  ]

  const gender = (genderKey) => GENDERS.find((entry) => entry.key === genderKey) || GENDERS[0]

  // --- Add-to-cart states -------------------------------------------------

  // Keys, not copy: the section owns the wording so a merchant can edit it.
  const ATC_STATES = {
    ADDED: 'added',
    ADDING: 'adding',
    UNAVAILABLE: 'unavailable',
    NEEDS_SIZE: 'needsSize',
    SOLD_OUT: 'soldOut',
    READY: 'ready'
  }

  // --- Sizes --------------------------------------------------------------

  // Shopify stores the adult sizes in long form on some products and short form
  // on others; the design always shows the short form.
  const SIZE_LABELS = {
    XXSmall: 'XXS',
    XSmall: 'XS',
    Small: 'S',
    Medium: 'M',
    Large: 'L',
    XLarge: 'XL',
    XXLarge: '2XL',
    XXXLarge: '3XL',
    '3XLarge': '3XL',
    XXS: 'XXS',
    XXL: '2XL',
    XXXL: '3XL'
  }

  const sizeLabel = (raw) => {
    if (!raw) return ''
    const value = String(raw)
    return SIZE_LABELS[value] || value
  }

  // --- Groups -------------------------------------------------------------

  const sizesFor = (groupKey, product) =>
    ((product && product.variants) || []).map((variant) => ({
      key: `${groupKey}:${variant.id}`,
      label: sizeLabel(variant.option1 || variant.title),
      variantId: variant.id,
      available: variant.available !== false
    }))

  // Kids first, then adults — the order the design lists the two size bands in.
  const productGroups = (products, genderKey) => {
    const source = products || {}
    const config = gender(genderKey)

    return [
      { key: 'kids', label: config.kidsLabel, product: source[config.kidsRole] },
      { key: 'adults', label: config.adultsLabel, product: source[config.adultsRole] }
    ]
      .filter((entry) => entry.product)
      .map((entry) => ({ ...entry, sizes: sizesFor(entry.key, entry.product) }))
      .filter((entry) => entry.sizes.length)
  }

  const findSize = (groups, sizeKey) => {
    if (!sizeKey) return null
    for (const group of groups || []) {
      const size = (group.sizes || []).find((entry) => entry.key === sizeKey)
      if (size) return { group, size }
    }
    return null
  }

  // The product whose media the gallery shows. Before a size is picked this is
  // the adult product, which is what the design mocks up.
  const activeProduct = (groups, sizeKey) => {
    const hit = findSize(groups, sizeKey)
    if (hit) return hit.group.product

    const list = groups || []
    const adults = list.find((group) => group.key === 'adults')
    if (adults) return adults.product
    return list.length ? list[0].product : null
  }

  // The bare size label, or '' with nothing selected. The section composes the
  // "Select size: L" line so the prefix stays merchant-editable.
  const selectedSizeLabel = (groups, sizeKey) => {
    const hit = findSize(groups, sizeKey)
    return hit ? hit.size.label : ''
  }

  // The `style: x` tag is what the shared size-guide modal keys off.
  const styleTag = (product) => {
    const tag = ((product && product.tags) || [])
      .map(String)
      .find((entry) => entry.toLowerCase().indexOf('style:') === 0)
    return tag ? tag.slice(tag.indexOf(':') + 1).trim().toLowerCase() : ''
  }

  // --- Add to cart --------------------------------------------------------

  const atcState = (state) => {
    const { groups = [], selectedKey = null, loading = false, success = false } = state || {}

    if (success) return { state: ATC_STATES.ADDED, disabled: true, soldOut: false }
    if (loading) return { state: ATC_STATES.ADDING, disabled: true, soldOut: false }
    if (!groups.length) return { state: ATC_STATES.UNAVAILABLE, disabled: true, soldOut: false }

    const hit = findSize(groups, selectedKey)
    if (!hit) return { state: ATC_STATES.NEEDS_SIZE, disabled: true, soldOut: false }
    if (!hit.size.available) return { state: ATC_STATES.SOLD_OUT, disabled: true, soldOut: true }

    return { state: ATC_STATES.READY, disabled: false, soldOut: false }
  }

  const api = {
    GENDERS,
    SIZE_LABELS,
    ATC_STATES,
    gender,
    sizeLabel,
    productGroups,
    findSize,
    activeProduct,
    selectedSizeLabel,
    styleTag,
    atcState
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api
  }
  if (global) {
    global.KnobbyRiskTakerEngine = api
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this)
